import React from "react";
import { Box, Typography, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "./JobpostingDetailsOverview.css";

import CandidateStatusChart from "./CandidateStatusChart";

const InfoItem = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "14px" }}>
      {label}
    </Typography>
    <Typography variant="body1">
      {value && value !== "" ? value : "-"}
    </Typography>
  </Grid>
);

const HtmlContent = ({ html }) => (
  <Box sx={{ fontSize: 14 }} dangerouslySetInnerHTML={{ __html: html }} />
);

const SkillsList = ({ skills }) => {
  const list = typeof skills === 'string' ? skills.split(",") : (skills || []);
  return (
    <Box>
      <Grid container spacing={1}>
        {list?.length > 0 && list?.map((item, i) => (
          <Grid item xs={6} key={i}>
            <Typography sx={{ mb: 0.3 }}>• {typeof item === 'string' ? item.trim() : item}</Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const JobpostingDetailsOverview = ({ selectedJob, jobDetails }) => {
  const [listSegments, setListSegments] = useState([]);

  const segmentList = useSelector((state) => state.GetListSegments.data);

  useEffect(() => {
    if (segmentList && segmentList.length > 0) {
      const activeSegments = segmentList
        .filter((segment) => segment.segmentStatus === 1)
        .map(({ id, segmentName }) => ({ id: id, label: segmentName }));
      setListSegments([]); // clear old values
      setTimeout(() => {
        setListSegments(activeSegments);
      }, 0); // ensure fresh render
    }
  }, [segmentList]);

  return (
    <Box>
      {/* JOB DETAILS & CANDIDATE STATUS SIDE-BY-SIDE */}
      <Grid container spacing={2} sx={{ mb: "17px" }}>
        {/* JOB DETAILS */}
        <Grid item xs={12} lg={7} xl={7}>
          <div className="job-card" style={{ height: "100%", marginBottom: 0 }}>
            <div className="job-card-header">
              <h2>Job Details</h2>
            </div>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={1}>
                <InfoItem label="Job Title*" value={jobDetails.jobTitle} />
                <InfoItem
                  label="External Job ID"
                  value={jobDetails.externalSystemID}
                />

                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "14px" }}>Job Status*</Typography>
                  <Box mt={0.5}>
                    <span
                      style={{
                        display: "inline-block",
                        backgroundColor: "#DCFCE7",
                        color: "#016630",
                        padding: "4px 8px",
                        borderRadius: "15px",
                        fontSize: 14,
                      }}
                    >
                      {jobDetails.jobStatus}
                    </span>
                  </Box>
                </Grid>

                <InfoItem
                  label="Job Type*"
                  value={jobDetails.jobType?.join(", ")}
                />
                <InfoItem label="Work Model*" value={jobDetails.workModel} />
                <InfoItem
                  label="Client"
                  value={jobDetails?.clientName?.clientName}
                />
                <InfoItem label="End Client" value={jobDetails?.endClientName} />

                <InfoItem
                  label="Client Bill Rate"
                  value={jobDetails.clientBillRate}
                />
                <InfoItem label="Pay rate/Salary*" value={jobDetails.payBillRate} />
                <InfoItem label="Location*" value={jobDetails.location} />
                <InfoItem label="Location Details" value={jobDetails.locationDetails} />

                {jobDetails?.recruiterTeam &&
                  jobDetails?.recruiterTeam.length > 0 &&
                  jobDetails?.recruiterTeam.map((data, index) => {
                    return (
                      <InfoItem
                        key={index}
                        label={data.key}
                        value={data.value ?? ""}
                      />
                    );
                  })}
                <InfoItem label="Priority" value={jobDetails.priority} />
                {listSegments.length > 0 ? (
                <InfoItem
                  label="Segment"
                  value={
                    jobDetails.segmentId && listSegments.length > 0
                      ? listSegments?.filter(
                        (x) => x.id === jobDetails.segmentId,
                      )?.[0]?.label || null
                      : null
                  }
                /> 
                ) : ("")}
                {jobDetails?.additionInfo &&
                  jobDetails?.additionInfo.length > 0 &&
                  jobDetails?.additionInfo.map((data, index) => {
                    return (
                      <InfoItem
                        key={index}
                        label={`${data.key}${Number(data.mandatory) === 1 ? "*" : ""}`}
                        value={data.value?.toString() || ""}
                      />
                    );
                  })}
              </Grid>
            </Box>
          </div>
        </Grid>

        {/* CANDIDATE STATUS */}
        <Grid item xs={12} lg={5} xl={5}>
          <div className="job-card" style={{ height: "100%", marginBottom: 0 }}>
            <div className="job-card-header">
              <h2>Candidate Status</h2>
            </div>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", height: "calc(100% - 57px)", overflowY: "auto" }}>
              <CandidateStatusChart applicantStatusCounts={jobDetails?.applicantStatusCounts} />
            </Box>
          </div>
        </Grid>
      </Grid>

      {/* JOB DESCRIPTION */}
      <div className="job-card">
        <div className="job-card-header">
          <h2>Job Description</h2>
        </div>
        <Box sx={{ p: 3 }}>
          <HtmlContent html={jobDetails.jobDesc} />
        </Box>
      </div>

      {/* SKILLS */}
      <Grid container spacing={2}>
        {/* PRIMARY SKILLS */}
        {jobDetails.primarySkills && (
          <Grid item xs={12} md={4}>
            <div className="job-card">
              <div className="job-card-header" style={{ width: "78%" }}>
                <h2>Primary Skills</h2>
              </div>
              <Box sx={{ p: 3 }} className="scrollable-card-content">
                <SkillsList skills={jobDetails.primarySkills} />
              </Box>
            </div>
          </Grid>
        )}

        {/* SECONDARY SKILLS */}
        {jobDetails.secondarySkills && (
          <Grid item xs={12} md={4}>
            <div className="job-card">
              <div className="job-card-header" style={{ width: "78%" }}>
                <h2>Secondary Skills</h2>
              </div>
              <Box sx={{ p: 3 }} className="scrollable-card-content">
                <SkillsList skills={jobDetails.secondarySkills} />
              </Box>
            </div>
          </Grid>
        )}
        {/* Job Description for social media*/}
        {jobDetails.jobDescForSM && (
          <Grid item xs={12} md={4}>
            <div className="job-card">
              <div className="job-card-header" style={{ width: "78%" }}>
                <h2>Job Description For Social Media</h2>
              </div>
              <Box sx={{ p: 3 }} className="scrollable-card-content">
                <HtmlContent html={jobDetails.jobDescForSM} />
              </Box>
            </div>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default JobpostingDetailsOverview;
